import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import logger from '../utils/logger';

// Create Context
const WordContext = createContext();

// Constants for supported languages - adding this at the top level to ensure consistency
export const SUPPORTED_LANGUAGES = ["Chinese", "French", "Italian", "Japanese", "Korean", "Spanish"];

// Helper function to decode JWT token
const getUserIdFromToken = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        
        const decodedToken = JSON.parse(jsonPayload);
        return decodedToken.sub || null;
    } catch (err) {
        logger.error("WordContext - Error decoding token:", err);
        return null;
    }
};

// Helper function to get userId from multiple sources
const getUserId = () => {
    // Always try to get the latest userId from the JWT token first
    const token = localStorage.getItem('access_token');
    if (token) {
        try {
            const userId = getUserIdFromToken(token);
            if (userId) {
                // Update localStorage with the current userId from the token
                localStorage.setItem('userId', userId);
                return userId;
            }
        } catch (err) {
            logger.error("WordContext - Error getting userId from token:", err);
        }
    }
    
    // Fall back to localStorage only if token extraction fails
    const localUserId = localStorage.getItem('userId');
    if (localUserId) {
        logger.debug("WordContext - Using userId from localStorage (fallback)");
        return localUserId;
    }
    
    logger.warn("WordContext - No userId available from token or localStorage");
    return null;
};

export const WordProvider = ({ children }) => {
    const [savedWords, setSavedWords] = useState({});
    const [savedWordsDetails, setSavedWordsDetails] = useState({});
    const [savedKanji, setSavedKanji] = useState([]);
    const [savedKanjiDetails, setSavedKanjiDetails] = useState({});
    const [savedHanzi, setSavedHanzi] = useState([]);
    const [savedHanziDetails, setSavedHanziDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [lastFetched, setLastFetched] = useState({});
    const [fetchStatus, setFetchStatus] = useState({
        inProgress: false,
        lastCompleted: null,
        error: null
    });
    
    // Separate Sets for Kanji and Hanzi
    const [kanjiSet, setKanjiSet] = useState(new Set());
    const [hanziSet, setHanziSet] = useState(new Set());
    
    // Update the Sets whenever savedKanji and savedHanzi change
    useEffect(() => {
        if (savedKanji && Array.isArray(savedKanji)) {
            setKanjiSet(new Set(savedKanji.map(k => k.trim())));
            logger.debug(`WordContext - Updated kanji set with ${savedKanji.length} characters`);
        }
    }, [savedKanji]);
    
    useEffect(() => {
        if (savedHanzi && Array.isArray(savedHanzi)) {
            setHanziSet(new Set(savedHanzi.map(h => h.trim())));
            logger.debug(`WordContext - Updated hanzi set with ${savedHanzi.length} characters`);
        }
    }, [savedHanzi]);
    
    // Check if Kanji/Hanzi is saved using Sets
    const isKanjiSaved = useCallback((kanji) => kanjiSet.has(kanji?.trim()), [kanjiSet]);
    const isHanziSaved = useCallback((hanzi) => hanziSet.has(hanzi?.trim()), [hanziSet]);

    // Get word grade (1-5) or default to 1
    const getWordGrade = useCallback((word, language) => savedWordsDetails[language]?.[word?.trim()]?.grade || 1, [savedWordsDetails]);

    // Get word known status
    const isWordKnown = useCallback((word, language) => savedWordsDetails[language]?.[word?.trim()]?.known || false, [savedWordsDetails]);

    // Get kanji grade (1-5) or default to 1
    const getKanjiGrade = useCallback((kanji) => savedKanjiDetails[kanji?.trim()]?.grade || 1, [savedKanjiDetails]);

    // Get kanji known status
    const isKanjiKnown = useCallback((kanji) => savedKanjiDetails[kanji?.trim()]?.known || false, [savedKanjiDetails]);

    // Get hanzi grade (1-5) or default to 1
    const getHanziGrade = useCallback((hanzi) => savedHanziDetails[hanzi?.trim()]?.grade || 1, [savedHanziDetails]);

    // Get hanzi known status
    const isHanziKnown = useCallback((hanzi) => savedHanziDetails[hanzi?.trim()]?.known || false, [savedHanziDetails]);
    
    const fetchSavedWords = async (language) => {
        try {
            const userId = getUserId();
            if (!userId) {
                logger.debug('WordContext - No user ID available, skipping word fetch');
                return { success: false, error: 'No user ID available' };
            }

            // Check if user is authenticated before making API calls
            if (!localStorage.getItem('access_token')) {
                logger.debug('WordContext - No access token available, skipping word fetch');
                return { success: false, error: 'No access token available' };
            }
            
            const currentLanguage = language || localStorage.getItem('learningLanguage') || 'Spanish';
            logger.debug(`WordContext - Fetching saved words for language: ${currentLanguage}`);
            
            const response = await apiClient.get(`/api/getUserWords?userId=${userId}&language=${currentLanguage}`);
            
            if (!response.ok) {
                logger.error(`WordContext - Failed to fetch saved words: ${response.status}`);
                throw new Error(`Failed to fetch saved words: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (!data.words || !Array.isArray(data.words)) {
                logger.error('WordContext - Invalid response format for saved words');
                throw new Error('Invalid response format for saved words');
            }
            
            // Extract just the words for the savedWords state
            const wordItems = data.words.map(w => w.item);
            
            // Create a details object with word details
            const wordDetails = {};
            data.words.forEach(word => {
                wordDetails[word.item] = {
                    grade: word.grade || 1,
                    known: word.known || false
                };
            });
            
            // Update states
            setSavedWords(prev => ({
                ...prev,
                [currentLanguage]: wordItems
            }));
            
            setSavedWordsDetails(prev => ({
                ...prev,
                [currentLanguage]: wordDetails
            }));
            
            setLastFetched(prev => ({
                ...prev,
                [currentLanguage]: new Date().getTime()
            }));
            
            logger.debug(`WordContext - Fetched ${wordItems.length} saved words for ${currentLanguage}`);
            
            return { success: true };
        } catch (error) {
            logger.error('WordContext - Error fetching saved words:', error);
            return { success: false, error: error.message };
        }
    };
    
    useEffect(() => {
        // Initial fetch for current language
        fetchSavedWords();
        
        // We're removing the polling interval to reduce unnecessary API calls
        // Instead, we'll rely on explicit refresh calls when needed
        // This prevents the constant API polling that's causing performance issues
    }, []);
    
    // Listen for language changes
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'learningLanguage') {
                fetchSavedWords();
                
                // Specially load kanji if switching to Japanese
                const newLanguage = e.newValue;
                if (newLanguage === 'Japanese') {
                    logger.debug('WordContext - Language changed to Japanese, ensuring kanji are loaded');
                    loadSavedKanji();
                }
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);
    
    // Helper function to load saved kanji directly (useful for refreshes)
    const loadSavedKanji = async () => {
        try {
            const userId = getUserId();
            if (!userId) {
                logger.warn('WordContext - No userId available for loadSavedKanji');
                return;
            }
            
            logger.debug('WordContext - Loading saved kanji');
            
            const response = await apiClient.get(`/api/getUserKanji?userId=${userId}`);
            
            if (!response.ok) {
                logger.error(`WordContext - Failed to fetch saved kanji: ${response.status}`);
                throw new Error(`Failed to fetch saved kanji: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (!data.kanji || !Array.isArray(data.kanji)) {
                logger.error('WordContext - Invalid response format for saved kanji');
                throw new Error('Invalid response format for saved kanji');
            }
            
            // Extract just the kanji items for the savedKanji state
            const kanjiItems = data.kanji.map(k => k.item);
            
            // Create a details object with kanji details
            const kanjiDetails = {};
            data.kanji.forEach(kanji => {
                kanjiDetails[kanji.item] = {
                    grade: kanji.grade || 1,
                    known: kanji.known || false
                };
            });
            
            // Trim any whitespace from kanji characters
            const trimmedKanji = kanjiItems.map(k => k.trim());
            setSavedKanji(trimmedKanji);
            setSavedKanjiDetails(kanjiDetails);
            
            logger.debug(`WordContext - Loaded ${trimmedKanji.length} saved kanji`);
        } catch (error) {
            logger.error('WordContext - Error loading saved kanji:', error);
        }
    };
    
    // On initial mount, check if we're using Japanese and load kanji separately
    useEffect(() => {
        const currentLanguage = localStorage.getItem('learningLanguage');
        if (currentLanguage === 'Japanese') {
            loadSavedKanji();
        }
    }, []);
    
    // Check if a word is saved - simplified to only check savedWords
    const isWordSaved = useCallback((word, language) => {
        if (!word) return false;
        
        const currentLanguage = language || localStorage.getItem('learningLanguage') || 'Spanish';
        const words = savedWords[currentLanguage];
        
        if (!words || !Array.isArray(words) || words.length === 0) return false;
        
        // For all languages, check if the exact word is saved
        const trimmedWord = word.trim();
        const exactMatch = words.some(w => w.trim() === trimmedWord);
        
        return exactMatch;
    }, [savedWords]);
    
    // Add Word - Corrected to include API call
    const addSavedWord = useCallback(async (word, language, details = { grade: 1, known: false }) => {
        const userId = getUserId();
        const trimmedWord = word?.trim();
        const targetLanguage = language || localStorage.getItem('learningLanguage') || 'Spanish'; // Determine language early
        
        if (!userId || !trimmedWord || !targetLanguage) {
            logger.error('[addSavedWord] Missing userId, word, or language', { userId, trimmedWord, targetLanguage });
            return false;
        }

        // Prevent adding if already saved in local state (optimistic check)
        if (savedWords[targetLanguage]?.includes(trimmedWord)) {
             logger.warn(`[addSavedWord] Word "${trimmedWord}" already present in local state for ${targetLanguage}. Skipping duplicate add.`);
             // Ensure details are consistent if somehow state got out of sync
             if (!savedWordsDetails[targetLanguage]?.[trimmedWord]) {
                 setSavedWordsDetails(prev => ({
                     ...prev,
                     [targetLanguage]: { ...(prev[targetLanguage] || {}), [trimmedWord]: details }
                 }));
             }
             return true; // Consider it successful as it's already saved locally
        }

        logger.debug(`WordContext - Attempting to save word via API: ${trimmedWord} (${targetLanguage})`);
        try {
            // --- Make the API call to save the word --- 
            const response = await apiClient.post('/api/saveUserWord', {
                 userId, 
                 language: targetLanguage, 
                 item: trimmedWord, 
                 grade: details.grade, 
                 known: details.known 
            });
            
            // apiClient.post already returns parsed JSON, no need to call .json()
            const result = response;

            // Check backend response explicitly for success message
            if (result && result.message?.includes("successfully")) {
                // Update local state *only* after successful API call
                setSavedWords(prev => ({
                     ...prev, 
                     [targetLanguage]: [...(prev[targetLanguage] || []), trimmedWord] 
                }));
                setSavedWordsDetails(prev => ({
                     ...prev, 
                     [targetLanguage]: { ...(prev[targetLanguage] || {}), [trimmedWord]: details } 
                }));
                logger.debug(`WordContext - Successfully saved word via API: ${trimmedWord}`);
                return true; // Indicate success
            } else {
                // Backend might return 200 OK but with an error message (e.g., validation)
                throw new Error(result?.message || 'Save word failed on backend');
            }
        } catch (error) {
            // Log the detailed error from apiClient or the thrown error
            logger.error(`WordContext - Error saving word ${trimmedWord} via API:`, error.message || error);
            return false; // Indicate failure
        }
    }, [savedWords]); // Dependency ensures we check against the latest saved words state

    // Remove Word - Assuming this already calls API correctly, keeping as is
    const removeSavedWord = useCallback(async (word, language) => {
        const userId = getUserId();
        const trimmedWord = word?.trim();
        const targetLanguage = language || localStorage.getItem('learningLanguage') || 'Spanish';
        if (!userId || !trimmedWord || !targetLanguage) return false;

        logger.debug(`WordContext - Attempting to remove word via API: ${trimmedWord} (${targetLanguage})`);
        try {
            const response = await apiClient.post('/api/removeUserWord', { userId, language: targetLanguage, item: trimmedWord });
            
            // apiClient.post already returns parsed JSON, no need to call .json()
            const result = response;

             if (result && result.message?.includes("successfully")) {
                // Update local state after successful API call
                setSavedWords(prev => ({ ...prev, [targetLanguage]: (prev[targetLanguage] || []).filter(w => w !== trimmedWord) }));
                setSavedWordsDetails(prev => {
                    const langDetails = { ...(prev[targetLanguage] || {}) };
                    delete langDetails[trimmedWord];
                    return { ...prev, [targetLanguage]: langDetails };
                });
                logger.debug(`WordContext - Successfully removed word via API: ${trimmedWord}`);
                return true;
            } else {
                 throw new Error(result?.message || 'Remove word failed on backend');
            }
        } catch (error) {
            logger.error(`WordContext - Error removing word ${trimmedWord} via API:`, error.message || error);
            return false;
        }
    }, []); // Removed savedWords dependency, rely on API call result
    
    // Add a kanji to saved kanji
    const addSavedKanji = (kanji) => {
        setSavedKanji(prev => {
            if (prev.includes(kanji.trim())) return prev;
            return [...prev, kanji.trim()];
        });
    };
    
    // Remove a kanji from saved kanji
    const removeSavedKanji = (kanji) => {
        setSavedKanji(prev => prev.filter(k => k !== kanji.trim()));
    };
    
    // Refresh all saved words for the current language or specified language
    const refreshSavedWords = async (specificLanguage = null) => {
        try {
            // Get the language to refresh - either the specified one or current language
            const currentLanguage = specificLanguage || localStorage.getItem('learningLanguage') || 'Spanish';
            logger.debug(`WordContext - Starting refresh for ${currentLanguage}`);
            
            // Only allow one refresh operation at a time
            if (fetchStatus.inProgress) {
                logger.warn('WordContext - Refresh already in progress, skipping duplicate call');
                return { success: false, error: 'Refresh already in progress' };
            }
            
            // Set loading and fetch status
            setLoading(true);
            setFetchStatus(prev => ({ ...prev, inProgress: true, error: null }));
            
            try {
                logger.debug(`WordContext - Refreshing ${currentLanguage}`);
                const result = await fetchSavedWords(currentLanguage);
                
                // DEBUG: For Japanese, log the saved kanji data specifically
                if (currentLanguage === 'Japanese') {
                    await loadSavedKanji(); // Refresh kanji specifically for Japanese
                    logger.debug(`WordContext - DEBUG - Current savedKanji: ${savedKanji.length} items`, 
                        savedKanji.length > 0 ? savedKanji.slice(0, 5) : 'empty');
                }
                
                logger.debug(`WordContext - Completed refresh for ${currentLanguage}:`, result);
                
                // Update fetch status
                setFetchStatus(prev => ({ 
                    ...prev, 
                    inProgress: false, 
                    lastCompleted: Date.now(),
                    error: null
                }));
                
                setLoading(false);
                
                // Dispatch an event that can be listened for by other contexts
                window.dispatchEvent(new CustomEvent('wordDataRefreshed', { 
                    detail: { timestamp: Date.now(), language: currentLanguage, result }
                }));
                
                return { success: true, language: currentLanguage };
            } catch (err) {
                logger.error(`WordContext - Failed to fetch words for ${currentLanguage}:`, err);
                
                // Update fetch status with error
                setFetchStatus(prev => ({ 
                    ...prev, 
                    inProgress: false,
                    error: err.message
                }));
                
                setLoading(false);
                
                return { success: false, error: err.message };
            }
        } catch (err) {
            logger.error('WordContext - Error in refreshSavedWords:', err);
            setLoading(false);
            return { success: false, error: err.message };
        }
    };
    
    // Update word grade
    const updateWordGrade = async (word, language, grade) => {
        try {
            const userId = getUserId();
            if (!userId) {
                logger.error('WordContext - No user ID available, cannot update word grade');
                return false;
            }
            
            const result = await apiClient.post('/api/updateWordGrade', {
                userId,
                language,
                item: word,
                grade
            });
            
            // The server returns {message: "Word grade updated successfully"} on success
            // and doesn't include a success property, so we need to check for message
            if (!result) {
                logger.error('WordContext - Failed to update word grade: No response received');
                return false;
            }
            
            // If there's a message about successful update, consider it successful
            if (result.message && result.message.includes("successfully")) {
                logger.debug(`WordContext - Word grade updated successfully: ${result.message}`);
                
                // Update local state
                setSavedWordsDetails(prev => ({
                    ...prev,
                    [language]: {
                        ...prev[language],
                        [word]: {
                            ...prev[language]?.[word],
                            grade
                        }
                    }
                }));
                
                return true;
            } else {
                logger.error(`WordContext - Failed to update word grade: ${result?.message || 'Invalid response'}`);
                return false;
            }
            
        } catch (error) {
            logger.error('WordContext - Error updating word grade:', error);
            return false;
        }
    };
    
    // Update word known status
    const updateWordKnown = async (word, language, known) => {
        try {
            const userId = getUserId();
            if (!userId) {
                logger.error('WordContext - No user ID available, cannot update word known status');
                return false;
            }
            
            const result = await apiClient.post('/api/updateWordKnown', {
                userId,
                language,
                item: word,
                known
            });
            
            // The server returns {message: "Word known status updated successfully"} on success
            // and doesn't include a success property, so we need to check for message
            if (!result) {
                logger.error('WordContext - Failed to update word known status: No response received');
                return false;
            }
            
            // If there's a message about successful update, consider it successful
            if (result.message && result.message.includes("successfully")) {
                logger.debug(`WordContext - Word known status updated successfully: ${result.message}`);
                
                // Update local state
                setSavedWordsDetails(prev => ({
                    ...prev,
                    [language]: {
                        ...prev[language],
                        [word]: {
                            ...prev[language]?.[word],
                            known
                        }
                    }
                }));
                
                return true;
            } else {
                logger.error(`WordContext - Failed to update word known status: ${result?.message || 'Invalid response'}`);
                return false;
            }
            
        } catch (error) {
            logger.error('WordContext - Error updating word known status:', error);
            return false;
        }
    };
    
    // Update kanji grade
    const updateKanjiGrade = async (kanji, grade) => {
        try {
            const userId = getUserId();
            if (!userId) {
                logger.error('WordContext - No user ID available, cannot update kanji grade');
                return false;
            }
            
            const result = await apiClient.post('/api/updateKanjiGrade', {
                userId,
                kanji,
                grade
            });
            
            // The server returns {message: "Kanji grade updated successfully"} on success
            // and doesn't include a success property, so we need to check for message
            if (!result) {
                logger.error('WordContext - Failed to update kanji grade: No response received');
                return false;
            }
            
            // If there's a message about successful update, consider it successful
            if (result.message && result.message.includes("successfully")) {
                logger.debug(`WordContext - Kanji grade updated successfully: ${result.message}`);
                
                // Update local state
                setSavedKanjiDetails(prev => ({
                    ...prev,
                    [kanji]: {
                        ...prev[kanji],
                        grade
                    }
                }));
                
                return true;
            } else {
                logger.error(`WordContext - Failed to update kanji grade: ${result?.message || 'Invalid response'}`);
                return false;
            }
            
        } catch (error) {
            logger.error('WordContext - Error updating kanji grade:', error);
            return false;
        }
    };
    
    // Update kanji known status
    const updateKanjiKnown = async (kanji, known) => {
        try {
            const userId = getUserId();
            if (!userId) {
                logger.error('WordContext - No user ID available, cannot update kanji known status');
                return false;
            }
            
            const result = await apiClient.post('/api/updateKanjiKnown', {
                userId,
                kanji,
                known
            });
            
            // The server returns {message: "Kanji known status updated successfully"} on success
            // and doesn't include a success property, so we need to check for message
            if (!result) {
                logger.error('WordContext - Failed to update kanji known status: No response received');
                return false;
            }
            
            // If there's a message about successful update, consider it successful
            if (result.message && result.message.includes("successfully")) {
                logger.debug(`WordContext - Kanji known status updated successfully: ${result.message}`);
                
                // Update local state
                setSavedKanjiDetails(prev => ({
                    ...prev,
                    [kanji]: {
                        ...prev[kanji],
                        known
                    }
                }));
                
                return true;
            } else {
                logger.error(`WordContext - Failed to update kanji known status: ${result?.message || 'Invalid response'}`);
                return false;
            }
            
        } catch (error) {
            logger.error('WordContext - Error updating kanji known status:', error);
            return false;
        }
    };
    
    // --- Fetch saved Hanzi (Chinese specific) --- Updated endpoint
    const loadSavedHanzi = async () => {
        const userId = getUserId();
        if (!userId || !localStorage.getItem('access_token')) return { success: false, error: 'Auth or user ID missing' };

        logger.debug('WordContext - Loading saved hanzi');
        try {
            // Use the new Hanzi endpoint
            const response = await apiClient.get(`/api/getUserHanzi?userId=${userId}`); // Corrected endpoint
            if (!response.ok) throw new Error(`Status ${response.status}`);
            const data = await response.json();
            if (!data.hanzi || !Array.isArray(data.hanzi)) throw new Error('Invalid hanzi data');

            const hanziItems = data.hanzi.map(h => h.item.trim());
            const hanziDetails = data.hanzi.reduce((acc, h) => {
                acc[h.item.trim()] = { grade: h.grade || 1, known: h.known || false };
                return acc;
            }, {});

            setSavedHanzi(hanziItems);
            setSavedHanziDetails(hanziDetails);
            logger.debug(`WordContext - Loaded ${hanziItems.length} saved hanzi`);
            return { success: true };
        } catch (error) {
            logger.error('WordContext - Error loading saved hanzi:', error);
            return { success: false, error: error.message };
        }
    };

    // --- Add Hanzi --- Updated endpoint
    const addSavedHanzi = useCallback(async (hanzi, details = { grade: 1, known: false }) => {
        const userId = getUserId();
        const trimmedHanzi = hanzi?.trim();
        if (!userId || !trimmedHanzi || trimmedHanzi.length !== 1) return false;

        logger.debug(`WordContext - Attempting to save Hanzi: ${trimmedHanzi}`);
        try {
            // Use the new Hanzi endpoint
            const result = await apiClient.post('/api/saveUserHanzi', { 
                userId, 
                hanzi: trimmedHanzi, 
                grade: details.grade, 
                known: details.known, 
                type: 'hanzi' 
            });

            // apiClient.post now returns the parsed JSON directly
            if (result && (result.success || result.message?.includes("successfully"))) {
                setSavedHanzi(prev => [...prev, trimmedHanzi]);
                setSavedHanziDetails(prev => ({ ...prev, [trimmedHanzi]: details }));
                logger.debug(`WordContext - Saved Hanzi: ${trimmedHanzi}`);
                return true;
            } else {
                throw new Error(result?.message || 'Save Hanzi failed');
            }
        } catch (error) {
            logger.error(`WordContext - Error saving Hanzi ${trimmedHanzi}:`, error);
            return false;
        }
    }, []);

    // --- Remove Hanzi --- ONLY updates local state
    const removeSavedHanzi = useCallback((hanzi) => {
        const trimmedHanzi = hanzi?.trim();
        if (!trimmedHanzi || trimmedHanzi.length !== 1) return; // Basic validation

        logger.debug(`WordContext - Updating state to remove Hanzi: ${trimmedHanzi}`);
        
        // Update the list of saved Hanzi characters
        setSavedHanzi(prev => prev.filter(h => h !== trimmedHanzi));
        
        // Update the details object for saved Hanzi
        setSavedHanziDetails(prev => {
            const details = { ...prev };
            delete details[trimmedHanzi];
            return details;
        });
        logger.debug(`WordContext - State updated to remove Hanzi: ${trimmedHanzi}`);
    }, []); // Removed async, removed API call, removed return value

    // --- Update Hanzi Grade --- Updated endpoint
    const updateHanziGrade = useCallback(async (hanzi, grade) => {
        const userId = getUserId();
        const trimmedHanzi = hanzi?.trim();
        if (!userId || !trimmedHanzi || trimmedHanzi.length !== 1) return false;

        logger.debug(`WordContext - Updating grade for Hanzi: ${trimmedHanzi} to ${grade}`);
        try {
            // Use the new Hanzi endpoint
            const result = await apiClient.post('/api/updateHanziGrade', { 
                userId, 
                hanzi: trimmedHanzi, 
                grade 
            });

            // apiClient.post now returns the parsed JSON directly
            if (result && (result.success || result.message?.includes("successfully"))) {
                setSavedHanziDetails(prev => ({ ...prev, [trimmedHanzi]: { ...(prev[trimmedHanzi] || {}), grade } }));
                logger.debug(`WordContext - Updated grade for Hanzi: ${trimmedHanzi}`);
                return true;
            } else {
                throw new Error(result?.message || 'Update Hanzi grade failed');
            }
        } catch (error) {
            logger.error(`WordContext - Error updating grade for Hanzi ${trimmedHanzi}:`, error);
            return false;
        }
    }, []);

    // --- Update Hanzi Known Status --- Updated endpoint
    const updateHanziKnown = useCallback(async (hanzi, known) => {
        const userId = getUserId();
        const trimmedHanzi = hanzi?.trim();
        if (!userId || !trimmedHanzi || trimmedHanzi.length !== 1) return false;

        logger.debug(`WordContext - Updating known status for Hanzi: ${trimmedHanzi} to ${known}`);
        try {
            // Use the new Hanzi endpoint
            const result = await apiClient.post('/api/updateHanziKnown', { 
                userId, 
                hanzi: trimmedHanzi, 
                known 
            });

            // apiClient.post now returns the parsed JSON directly
            if (result && (result.success || result.message?.includes("successfully"))) {
                setSavedHanziDetails(prev => ({ ...prev, [trimmedHanzi]: { ...(prev[trimmedHanzi] || {}), known } }));
                logger.debug(`WordContext - Updated known status for Hanzi: ${trimmedHanzi}`);
                return true;
            } else {
                throw new Error(result?.message || 'Update Hanzi known status failed');
            }
        } catch (error) {
            logger.error(`WordContext - Error updating known status for Hanzi ${trimmedHanzi}:`, error);
            return false;
        }
    }, []);

    // Add this useEffect at the end of the component
    useEffect(() => {
        const initializeData = async () => {
            logger.debug('WordContext - Initializing data for all languages on mount');
            setLoading(true);
            
            try {
                // Fetch data for all supported languages
                for (const lang of SUPPORTED_LANGUAGES) {
                    await fetchSavedWords(lang);
                }
                
                // Special handling for Japanese to load kanji
                await loadSavedKanji();
                
                // Special handling for Chinese to load hanzi
                await loadSavedHanzi();
                
                logger.debug('WordContext - Initial data loading complete');
            } catch (error) {
                logger.error('WordContext - Error loading initial data:', error);
            }
            
            setLoading(false);
        };
        
        initializeData();
    }, []);
    
    return (
        <WordContext.Provider
            value={{
                savedWords,
                savedKanji,
                loading,
                fetchStatus,
                isWordSaved,
                isKanjiSaved,
                addSavedWord,
                removeSavedWord,
                addSavedKanji,
                removeSavedKanji,
                refreshSavedWords,
                supportedLanguages: SUPPORTED_LANGUAGES,
                getWordGrade,
                isWordKnown,
                getKanjiGrade,
                isKanjiKnown,
                updateWordGrade,
                updateWordKnown,
                updateKanjiGrade,
                updateKanjiKnown,
                // --- Hanzi state and functions ---
                savedHanzi,
                isHanziSaved,
                addSavedHanzi,
                removeSavedHanzi,
                getHanziGrade,
                isHanziKnown,
                updateHanziGrade,
                updateHanziKnown
            }}
        >
            {children}
        </WordContext.Provider>
    );
};

// Custom hook to use the WordContext
export const useWordContext = () => {
    const context = useContext(WordContext);
    
    if (!context) {
        throw new Error('useWordContext must be used within a WordProvider');
    }
    
    return context;
};

export default WordContext;